import * as React from 'react';
import PropTypes from 'prop-types';
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default:
          "bg-sky-100 text-sky-700 border border-sky-200 hover:bg-sky-200",
        destructive:
          "bg-rose-100 text-rose-700 border border-rose-200 hover:bg-rose-200",
        success:
          "bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-200",
        warning:
          "bg-orange-100 text-orange-700 border border-orange-200 hover:bg-orange-200",
        neutral:
          "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200",
        outline:
          "border border-gray-200 bg-transparent hover:bg-gray-100",
        secondary:
          "bg-purple-100 text-purple-700 border border-purple-200 hover:bg-purple-200",
        ghost:
          "hover:bg-gray-100",
        link: "text-sky-700 underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'default',
    'destructive',
    'success',
    'warning',
    'neutral',
    'outline',
    'secondary',
    'ghost',
    'link',
  ]),
  size: PropTypes.oneOf(['default', 'sm', 'lg', 'icon']),
  asChild: PropTypes.bool,
};

export { Button, buttonVariants }
